import { observer } from 'mobx-react';
import { useParams } from 'react-router-dom';
import { Page, Pagebar, PagebarActions, PagebarTitle } from '@monorepo/base/src/components/page/page';
import { useStores } from '@monorepo/controlled/src/hooks/use-stores';
import routes from '../../../../modules/routes/index.routes';
import { useCampaignActions } from '../../../../modules/hooks/actions/campaign.actions';
import { useRoute } from '@monorepo/tools/src/lib/hooks/tools/use-route';
import { ControlledTable } from '@monorepo/controlled/src/components/table-wrapper/table-wrapper';
import { useCampaignHeaders } from '../../../../modules/hooks/theaders/campaigns.headers';
import { ControlledDater } from '@monorepo/controlled/src/components/controlled-dater/controlled-dater';
import { IMFPStore } from '../../../../modules/stores';
import {
	Entities,
	IPerformanceOptionsFilterType,
	IPerformanceOptionsSortBy,
	PerformancesColumns,
	useReportsList,
} from '../../../../modules/hooks/apis/use-reports-list';
import { PrimaryLink } from '@monorepo/base/src/components/buttons/buttons';
import styles from './../../../../styles/pages.module.scss';
import { useMetricsGet } from '../../../../modules/hooks/tools/use-metrics-get';
import { useMetrics } from '../../../../modules/hooks/tools/use-metrics';
import { availablePerformanceToMetrics } from '../../../../modules/models/performance.model';
import { SpacerY } from '@monorepo/base/src/components/spacer/spacer';
import { useAdvertiserActions } from '../../../../modules/hooks/actions/advertiser.actions';
import { EntityActionsMenu } from '../../../../modules/components/entity-actions-menu/entity-actions-menu';
import { DataAttribute } from '@monorepo/tools/src/lib/models/data-attr.model';
import { ManifestoChart } from '../../../../modules/components/chart/manifesto-chart';
import { EmptyCampaignsState } from '../../../../modules/components/empty-campaigns-state/empty-campaigns-state';
import { Helmet } from 'react-helmet-async';
import { PaginationState } from '@tanstack/react-table';
import { isFetchFromBackend } from '../../../../modules/helpers/reports.helpers';
import { isAbortError } from '@monorepo/tools/src/lib/tools/ask/guards';
import { usePageSettings } from '@monorepo/tools/src/lib/hooks/tools/use-page-settings';

export const AdvertiserCampaigns = observer(() => {
	const { campaignStore, advertiserStore } = useStores<IMFPStore>();
	const { advertiserId } = useParams();
	const { currentRouteWithoutSpecialChars } = useRoute();

	const reportsStore = campaignStore.getReportsStore();
	const reportData = reportsStore.getData();
	const metricsStore = campaignStore.getMetricsStore();
	const currentSegments = campaignStore.getSegments();
	const advertiserName = advertiserStore.getCrud().getData().getName();
	const defaultSortBy = usePageSettings().tableStore?.getSortingBy();

	if (!advertiserId) {
		return null;
	}

	const { reportsError, fetchReports } = useReportsList(
		reportsStore,
		{
			entity: Entities.Campagins,
			filters: [
				{
					column: PerformancesColumns.AdvertiserId,
					filterType: IPerformanceOptionsFilterType.Include,
					value: [advertiserId],
				},
			],
			groupBys: [PerformancesColumns.CampaignId],
			sortBys: defaultSortBy?.map(sort => {
				return {
					column: sort.id as IPerformanceOptionsSortBy,
					order: sort.desc ? 'desc' : 'asc',
				};
			}),
		},
		undefined,
		currentSegments
	);

	const { metricsError, fetchMetrics } = useMetricsGet(metricsStore, {
		entity: Entities.Chart,
		groupBys: [PerformancesColumns.Date],
		sortBys: defaultSortBy?.map(sort => {
			return {
				column: sort.id as IPerformanceOptionsSortBy,
				order: sort.desc ? 'desc' : 'asc',
			};
		}),
		filters: [
			{
				column: PerformancesColumns.AdvertiserId,
				filterType: IPerformanceOptionsFilterType.Include,
				value: [advertiserId ? advertiserId : '1'],
			},
		],
	});

	const { columns } = useCampaignHeaders({ summary: reportData?.getSummary() });
	const { SelectedActions, FiltersActions, SegmentActions } = useCampaignActions({ currentSegments });
	const { EntityActions } = useAdvertiserActions();
	const advertiser = advertiserStore.getCrud().getData();
	const { metrics, xLabels } = useMetrics(metricsStore.getData());

	return (
		<Page unstyled={true} className={styles.article}>
			<Helmet>
				<title>{advertiserName || 'Advertiser'} - Campaign</title>
			</Helmet>
			<Pagebar classes={{ pagebar: styles.padding }}>
				<PagebarTitle>Campaigns</PagebarTitle>
				<PagebarActions>
					<ControlledDater />
					<PrimaryLink icon={'plus'} to={advertiserId ? routes.advertisers.createCampaign(advertiserId) : ''} width={'148px'}>
						New Campaign
					</PrimaryLink>
					<EntityActionsMenu title={'Advertiser Actions'}>
						<EntityActions entity={advertiser} />
					</EntityActionsMenu>
				</PagebarActions>
			</Pagebar>
			<ManifestoChart
				isLoading={metricsStore.getIsLoading()}
				metrics={metrics}
				labels={xLabels}
				legendOptions={Object.keys(availablePerformanceToMetrics)}
				isError={!!metricsError && !isAbortError(metricsError)}
			/>
			<SpacerY y={2} />
			<ControlledTable
				currentRoute={currentRouteWithoutSpecialChars}
				columns={columns}
				classes={{ wrapper: styles.padding }}
				emptyState={
					<EmptyCampaignsState to={advertiserId ? routes.advertisers.createCampaign(advertiserId) : routes.campaigns.create()} />
				}
				debugProps={{ dataAttrs: [new DataAttribute('id', 'campaigns_advertiser_table')] }}
				data={reportData?.getData() || []}
				isLoading={reportsStore.getIsLoading()}
				isWithPerformance={true}
				isLoadingPerformance={reportsStore.getIsLoading()}
				defaultSortBy={defaultSortBy}
				fetchReports={fetchReports}
				fetchMetrics={fetchMetrics}
				onPageChange={(state: PaginationState) => {
					if (isFetchFromBackend(reportData, state) && !reportsStore.getIsLoading()) {
						fetchReports({ appendData: true, resetOffset: false });
					}
				}}
				isError={!!reportsError && !isAbortError(reportsError)}>
				{({ sortedRows }) => {
					campaignStore.setSortedRows(sortedRows);

					return {
						FiltersActions,
						SelectedActions,
						SegmentActions,
					};
				}}
			</ControlledTable>
		</Page>
	);
});
